
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { Company, SiteSettings } from '../types';
import { supabase } from '@/integrations/supabase/client';

export const useCompanyStore = create<{
  companies: Company[];
  addCompany: (companyData: Partial<Company>) => Promise<Company | null>;
  updateCompany: (id: string, updates: Partial<Company>) => Promise<boolean>;
  fetchCompanies: () => Promise<Company[]>;
  siteSettings: SiteSettings;
  fetchSiteSettings: () => Promise<SiteSettings>;
  updateSiteSettings: (settings: Partial<SiteSettings>) => Promise<boolean>;
}>((set, get) => ({
  companies: [],
  
  // Modified to save companies to Supabase
  addCompany: async (companyData) => {
    try {
      const newCompany = {
        id: uuidv4(),
        createdAt: Date.now(),
        isVisible: true,
        ...companyData
      } as Company;
      
      // Save to Supabase
      const { data, error } = await supabase
        .from('companies')
        .insert({
          id: newCompany.id,
          name: newCompany.name,
          locations: newCompany.locations,
          bio: newCompany.bio,
          staff_size: newCompany.staffSize,
          logo: newCompany.logo,
          banner_image: newCompany.bannerImage,
          website: newCompany.website,
          twitter_url: newCompany.twitterUrl,
          instagram_url: newCompany.instagramUrl,
          linkedin_url: newCompany.linkedinUrl,
          is_visible: newCompany.isVisible,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
        
      if (error) {
        console.error("Error saving company to Supabase:", error);
        return null;
      }
      
      // Update local cache
      set((state) => ({
        companies: [...state.companies, newCompany]
      }));
      
      return newCompany;
    } catch (error) {
      console.error("Error in addCompany:", error);
      return null;
    }
  },
  
  // Modified to update company in Supabase
  updateCompany: async (id, updates) => {
    try {
      // Format updates for Supabase
      const supabaseUpdates: any = {};
      
      if (updates.name) supabaseUpdates.name = updates.name;
      if (updates.locations) supabaseUpdates.locations = updates.locations;
      if (updates.bio !== undefined) supabaseUpdates.bio = updates.bio;
      if (updates.staffSize !== undefined) supabaseUpdates.staff_size = updates.staffSize;
      if (updates.logo !== undefined) supabaseUpdates.logo = updates.logo;
      if (updates.bannerImage !== undefined) supabaseUpdates.banner_image = updates.bannerImage;
      if (updates.website !== undefined) supabaseUpdates.website = updates.website;
      if (updates.twitterUrl !== undefined) supabaseUpdates.twitter_url = updates.twitterUrl;
      if (updates.instagramUrl !== undefined) supabaseUpdates.instagram_url = updates.instagramUrl;
      if (updates.linkedinUrl !== undefined) supabaseUpdates.linkedin_url = updates.linkedinUrl;
      if (updates.isVisible !== undefined) supabaseUpdates.is_visible = updates.isVisible;
      
      // Update in Supabase
      const { error } = await supabase
        .from('companies')
        .update(supabaseUpdates)
        .eq('id', id);
        
      if (error) {
        console.error("Error updating company in Supabase:", error);
        return false;
      }
      
      // Update local state
      set((state) => ({
        companies: state.companies.map((company) => 
          company.id === id ? { ...company, ...updates } : company
        )
      }));
      
      return true;
    } catch (error) {
      console.error("Error in updateCompany:", error);
      return false;
    }
  },
  
  // New method to fetch companies from Supabase
  fetchCompanies: async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('name', { ascending: true });
        
      if (error) {
        console.error("Error fetching companies from Supabase:", error);
        return get().companies;
      }
      
      // Convert Supabase data to our app's Company format
      const formattedCompanies: Company[] = data.map(company => ({
        id: company.id,
        name: company.name,
        locations: company.locations,
        createdAt: new Date(company.created_at).getTime(),
        bio: company.bio || '',
        staffSize: company.staff_size || '',
        logo: company.logo || '',
        bannerImage: company.banner_image || '',
        website: company.website || '',
        twitterUrl: company.twitter_url || '',
        instagramUrl: company.instagram_url || '',
        linkedinUrl: company.linkedin_url || '',
        isVisible: company.is_visible
      }));
      
      // Update local cache
      set({ companies: formattedCompanies });
      
      return formattedCompanies;
    } catch (error) {
      console.error("Error in fetchCompanies:", error);
      return get().companies;
    }
  },
  
  siteSettings: {
    instagramUrl: 'https://instagram.com/redflags',
    twitterUrl: 'https://twitter.com/redflags',
    linkedinUrl: 'https://linkedin.com/company/redflags'
  },
  
  // New method to fetch site settings from Supabase
  fetchSiteSettings: async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (error) {
        console.error("Error fetching site settings from Supabase:", error);
        return get().siteSettings;
      }
      
      const settings: SiteSettings = {
        instagramUrl: data.instagram_url || '',
        twitterUrl: data.twitter_url || '',
        linkedinUrl: data.linkedin_url || ''
      };
      
      // Update local cache
      set({ siteSettings: settings });
      
      return settings;
    } catch (error) {
      console.error("Error in fetchSiteSettings:", error);
      return get().siteSettings;
    }
  },
  
  // Modified to update site settings in Supabase
  updateSiteSettings: async (settings) => {
    try {
      // Get the existing settings first
      const { data, error: fetchError } = await supabase
        .from('site_settings')
        .select('id')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
        
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned" error
        console.error("Error fetching site settings ID:", fetchError);
        return false;
      }
      
      const settingsId = data?.id;
      
      if (settingsId) {
        // Update existing record
        const { error } = await supabase
          .from('site_settings')
          .update({
            instagram_url: settings.instagramUrl,
            twitter_url: settings.twitterUrl,
            linkedin_url: settings.linkedinUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', settingsId);
          
        if (error) {
          console.error("Error updating site settings in Supabase:", error);
          return false;
        }
      } else {
        // Create new record
        const { error } = await supabase
          .from('site_settings')
          .insert({
            instagram_url: settings.instagramUrl,
            twitter_url: settings.twitterUrl,
            linkedin_url: settings.linkedinUrl
          });
          
        if (error) {
          console.error("Error creating site settings in Supabase:", error);
          return false;
        }
      }
      
      // Update local state
      set((state) => ({
        siteSettings: {
          ...state.siteSettings,
          ...settings
        }
      }));
      
      return true;
    } catch (error) {
      console.error("Error in updateSiteSettings:", error);
      return false;
    }
  }
}));
